<template>
    <div class="contact-us-form">
        <loading :active.sync="isSubmitting"
                 :is-full-page="false"/>
        <Form @submit="onSubmit">
            <div v-for="field in schema.fields" class="mb-3" :key="field.name">
                <template v-if="field.type === 'checkbox'">
                    <template v-if="field.options.length">
                        <p>{{field.label}}</p>
                        <template v-for="(option, index) in field.options" v-key="index">
                            <label class="form-check">
                                <Field
                                    :type="field.type"
                                    :id="field.name"
                                    :name="field.name"
                                    :rules="field.rules"
                                    :value="option"
                                    class="form-check-input" />
                                <em>{{option}}</em>
                            </label>
                        </template>
                    </template>
                    <label v-else class="form-check">
                        <Field
                            :type="field.type"
                            :id="field.name"
                            :name="field.name"
                            :label="field.label"
                            :rules="field.rules"
                            :value="1"
                            class="form-check-input" />
                        <em>{{field.label}}</em>
                    </label>
                </template>
                <Field
                    v-else
                    :type="field.type"
                    :as="field.as"
                    :id="field.name"
                    :name="field.name"
                    :label="field.label"
                    :rules="field.rules"
                    :placeholder="field.label"
                    :value="field.value"
                    class="form-control" />
                <ErrorMessage :name="field.name" />
            </div>

            <button :disabled="isSubmitting" type="submit" class="btn btn-dark btn-lg">{{ submitLabel }}</button>
        </Form>

        <div class="alert" v-if="isSent || hasError.length">
            <span role="button" class="close" @click="isSent = false; hasError = ''">
                <font-awesome-icon :icon="['fa', 'times']"/>
            </span>
            <div class="message" v-if="hasError.length">
                <font-awesome-icon :icon="['fa', 'times']"/>
                <div>
                    {{hasError}}
                </div>
            </div>
            <div class="message" v-else>
                <font-awesome-icon :icon="['fa', 'check']"/>
                <div>
                    Message sent!
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import {
        Form,
        Field,
        ErrorMessage,
        configure,
        defineRule,
    } from 'vee-validate';
    import { localize, setLocale } from '@vee-validate/i18n';
    import { email, required } from '@vee-validate/rules';
    import en from '@vee-validate/i18n/dist/locale/en.json';
    import de from '@vee-validate/i18n/dist/locale/de.json';
    import ru from '@vee-validate/i18n/dist/locale/ru.json';
    import zhCN from '@vee-validate/i18n/dist/locale/zh_CN.json';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import '../font-awesome';

    defineRule('email', email);
    defineRule('required', required);

    configure({
        generateMessage: localize({
            en,
            de,
            ru,
            ua: ru,
            zh: zhCN,
        }),
    });

    export default defineComponent({
        name: 'ContactUsForm',
        components: {
            Form, Field, ErrorMessage, FontAwesomeIcon,
        },
        props: {
            submitLabel: {
                type: String,
                default: 'Submit',
            },
            locale: {
                type: String,
                default: 'en',
            },
            slug: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                schema: {
                    fields: [],
                },
                isSubmitting: false,
                isSent: false,
                hasError: '',
            };
        },
        methods: {
            onSubmit(values, { resetForm }) {
                this.isSubmitting = true;
                this.hasError = '';
                const formData = new FormData();
                Object.keys(values).map((key) => {
                    formData.append(key, values[key]);
                });
                const body = new URLSearchParams(formData);
                fetch('/api/forms', {
                    method: 'POST',
                    body,
                }).then((response) => {
                    if (response.status < 300) {
                        resetForm();
                        this.getFormFields();
                    } else {
                        this.hasError = response.statusText;
                    }
                    // eslint-disable-next-line no-return-assign
                    window.setTimeout(() => this.isSent = true, 1000);
                }).catch((error) => {
                    this.hasError = 'Error!';
                }).finally(() => {
                    window.setTimeout(() => {
                        this.isSubmitting = false;
                    }, 1000);
                });
            },
            getFormFields() {
                fetch(`/api/forms/${this.slug}`).then(async (response) => {
                    return await response.json();
                }).then(({data}) => {
                    this.schema.fields = data.fields.data.map((field) => {
                        return {
                            type: field.type,
                            as: field.type === 'textarea' ? field.type : '',
                            name: field.name,
                            label: field.label,
                            rules: field.rules.join('|'),
                            options: Array.isArray(field.options)
                                ? field.options
                                : [],
                        };
                    });
                    this.schema.fields.push({
                        type: 'hidden',
                        name: 'form_id',
                        value: data.id,
                    });
                    this.schema.fields.push({
                        type: 'hidden',
                        name: 'referer',
                        value: window.location.href,
                    });
                }).catch((error) => {
                    console.log(error);
                });
            },
        },
        created() {
            this.getFormFields();
        },
        mounted() {
            setLocale(this.locale);
        },
    });
</script>

<style lang="scss">
    @import "~bootstrap/scss/vendor/rfs";
    @import '../../scss/variables';

    .contact-us-form {
        position: relative;
        .form-control {
            border: 2px solid $blue-color;
            border-radius: 10px;
            color: $dark-blue-color;
            &::placeholder {
                color: $blue-color;
            }
            &:focus {
                border-color: $dark-blue-color;
                box-shadow: none;
            }
        }
        span[role="alert"] {
            font-size: small;
            color: $warning-color;
        }
        .alert {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border: 2px solid $light-blue-color;
            background: #fff;
            border-radius: 10px;
            margin: 0;
            .close {
                position: absolute;
                top: 10px;
                right: 10px;
                color: $light-blue-color;
                @include font-size(20px);
                line-height: 20px;
                &:hover {
                    color: $dark-blue-color;
                }
            }
            .message {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                height: 100%;
                text-transform: uppercase;
                color: $light-blue-color;
                @include font-size(30px);
                .fa-check, .fa-times {
                    @include font-size(40px);
                }
            }
        }
    }
</style>
